import React, { useState, useEffect } from 'react';
import WorkflowFlow from './WorkflowFlow';
import MRTTableAdapter from './MRTTableAdapter';
import { createMRTColumnHelper } from 'material-react-table';
import { API_BASE } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface WorkflowCase {
  id: number;
  adverse_event_id: number;
  current_stage: string;
  current_task: string; // Changed from active_task to current_task
  current_assignee_id: number | null;
  workflow_started_at: string;
  current_stage_started_at: string;
  sla_deadline: string | null;
  is_overdue: boolean;
  is_urgent: boolean;
  priority_level: string;
  created_at: string;
  record_id: string; // Record identifier displayed in the table
  record_type: string; // Added for record type
  is_locked: boolean; // Added for record locking
  last_updated: string; // Added for last updated tracking
  source?: string; // Origin (e.g., literature, user)
  adverse_event?: {
    id: number;
    description: string;
    severity: string;
    is_serious: boolean;
    reported_by: string;
    report_source?: string;
    death?: boolean;
    life_threatening?: boolean;
    product_name?: string; // Added for product name
    product_id?: number; // Added for product ID
  };
  current_assignee?: {
    id: number;
    full_name: string;
  };
}

const WorkflowCaseList: React.FC = () => {
  const [workflows, setWorkflows] = useState<WorkflowCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter] = useState<string>('all');
  const [success, setSuccess] = useState<string>('');
  const [error, setError] = useState<string>('');
  const possibleStages = ['draft','in_review','qc','submitted','closed'];
  const [viewingCaseId, setViewingCaseId] = useState<number | null>(null);
  const [viewingCase, setViewingCase] = useState<any | null>(null);
  const [viewMode, setViewMode] = useState<'details' | 'transition'>('details');
  const [transitionStage, setTransitionStage] = useState<string>('');
  const [transitionReason, setTransitionReason] = useState<string>('');
  const [transitionAssignee, setTransitionAssignee] = useState<string>('');
  
  // Workflow creation modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    adverse_event_id: '',
    initial_assignee_id: '',
    priority_level: 'normal'
  });
  const [adverseEvents, setAdverseEvents] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const diagramStages = [
    'Register ICSR information',
    'Review',
    'Quality Check',
    'Submission',
    'Completed'
  ];
  const uiToBackendStage: Record<string, string> = {
    'Register ICSR information': 'draft',
    'Review': 'in_review',
    'Quality Check': 'qc',
    'Submission': 'submitted',
    'Completed': 'closed'
  };
  useAuth();
  
  // Advanced table filters
  const [filters, setFilters] = useState({
    completedRecords: true,
    inWorkflowRecords: true,
    assignedToOthers: false
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [currentPage] = useState(1);
  const [sortKey] = useState<string>('created_at');
  const [stageFilter, setStageFilter] = useState<string>('');
  const [sortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Bulk operations state
  const [selectedWorkflows, setSelectedWorkflows] = useState<number[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkAction, setBulkAction] = useState<string>('');
  const [bulkAssignee, setBulkAssignee] = useState<string>('');
  const [bulkPriority, setBulkPriority] = useState<string>('');

  // Centralized API base

  useEffect(() => {
    fetchWorkflows();
    fetchAdverseEvents();
    fetchUsers();
    const handler = (e: any) => {
      const s = e?.detail?.machine;
      if (typeof s === 'string') setStageFilter(s);
    };
    window.addEventListener('workflow_stage_filter', handler as any);
    return () => window.removeEventListener('workflow_stage_filter', handler as any);
  }, []);

  const fetchWorkflows = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch(`${API_BASE}/workflow/cases/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const payload = await response.json();
        const list = Array.isArray(payload) ? payload : (payload?.data ?? []);
        // Normalize backend response to expected shape and add safe defaults
        const normalized: WorkflowCase[] = list.map((d: any) => ({
          id: d.id,
          adverse_event_id: d.adverse_event_id ?? d.id,
          current_stage: d.current_stage ?? d.status ?? 'draft',
          current_assignee_id: d.current_assignee_id ?? null,
          workflow_started_at: d.workflow_started_at ?? d.created_at ?? new Date().toISOString(),
          current_stage_started_at: d.current_stage_started_at ?? d.created_at ?? new Date().toISOString(),
          sla_deadline: d.sla_deadline ?? null,
          is_overdue: Boolean(d.is_overdue),
          is_urgent: Boolean(d.is_urgent) || (d.priority === 'high' || d.priority === 'urgent'),
          priority_level: d.priority_level ?? d.priority ?? 'normal',
          created_at: d.created_at ?? new Date().toISOString(),
          adverse_event: d.adverse_event ? {
            id: d.adverse_event.id ?? d.id,
            description: d.adverse_event.description ?? (d.title ?? ''),
            severity: d.adverse_event.severity ?? 'mild',
            is_serious: Boolean(d.adverse_event.is_serious),
            reported_by: d.adverse_event.reported_by ?? '',
            death: Boolean(d.adverse_event.death),
            life_threatening: Boolean(d.adverse_event.life_threatening),
            product_name: d.adverse_event.product_name, // Added product_name
            product_id: d.adverse_event.product_id // Added product_id
          } : undefined,
          current_assignee: d.current_assignee ? {
            id: d.current_assignee.id,
            full_name: d.current_assignee.full_name ?? ''
          } : undefined
        }));
        setWorkflows(normalized);
      } else {
        console.error('Failed to fetch workflows:', response.status);
      }
    } catch (error) {
      console.error('Error fetching workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdverseEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await fetch(`${API_BASE}/adverse-events/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAdverseEvents(Array.isArray(data) ? data : (data?.data || []));
      }
    } catch (error) {
      console.error('Error fetching adverse events:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await fetch(`${API_BASE}/users/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(Array.isArray(data) ? data : (data?.data || []));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateWorkflow = () => {
    setShowCreateModal(true);
    setCreateFormData({
      adverse_event_id: '',
      initial_assignee_id: '',
      priority_level: 'normal'
    });
  };


  const handleSubmitCreateWorkflow = async () => {
    if (!createFormData.adverse_event_id) {
      setError('Please select an adverse event');
        return;
      }

    setLoadingCreate(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in.');
        return;
      }

      const payload = {
        adverse_event_id: Number(createFormData.adverse_event_id),
        initial_assignee_id: createFormData.initial_assignee_id ? Number(createFormData.initial_assignee_id) : undefined,
        priority_level: createFormData.priority_level
      };

      const response = await fetch(`${API_BASE}/workflow/cases/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Failed with ${response.status}`);
      }

      await fetchWorkflows();
      setSuccess('Workflow created successfully');
      setShowCreateModal(false);
    } catch (err: any) {
      console.error(err);
      setError(`Create workflow failed: ${err.message || err}`);
    } finally {
      setLoadingCreate(false);
    }
  };

  // Bulk operations functions
  const handleWorkflowSelect = (workflowId: number, checked: boolean) => {
    if (checked) {
      setSelectedWorkflows(prev => [...prev, workflowId]);
    } else {
      setSelectedWorkflows(prev => prev.filter(id => id !== workflowId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedWorkflows(paginatedWorkflows.map(w => w.id));
    } else {
      setSelectedWorkflows([]);
    }
  };

  const handleBulkAction = async () => {
    if (selectedWorkflows.length === 0) {
      setError('Please select workflows to perform bulk action');
      return;
    }

    if (!bulkAction) {
      setError('Please select an action to perform');
      return;
    }

    setLoadingCreate(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in.');
        return;
      }

      const promises = selectedWorkflows.map(async (workflowId) => {
        const payload: any = {};
        
        if (bulkAction === 'assign') {
          if (!bulkAssignee) {
            throw new Error('Please select an assignee');
          }
          payload.assignee_id = Number(bulkAssignee);
        } else if (bulkAction === 'priority') {
          if (!bulkPriority) {
            throw new Error('Please select a priority');
          }
          payload.priority_level = bulkPriority;
        } else if (bulkAction === 'advance') {
          payload.action = 'advance';
        }

        const response = await fetch(`${API_BASE}/workflow/cases/${workflowId}/transition`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Workflow ${workflowId}: ${text}`);
        }

        return response;
      });

      await Promise.all(promises);
      await fetchWorkflows();
      setSuccess(`Bulk action completed successfully on ${selectedWorkflows.length} workflows`);
      setSelectedWorkflows([]);
      setShowBulkActions(false);
      setBulkAction('');
      setBulkAssignee('');
      setBulkPriority('');
    } catch (err: any) {
      console.error(err);
      setError(`Bulk action failed: ${err.message || err}`);
    } finally {
      setLoadingCreate(false);
    }
  };


  const handleTransition = async (workflowId: number, currentStage: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const to_stage = prompt(`Enter next stage (current: ${currentStage}). Options: ${possibleStages.join(', ')}`);
      if (!to_stage) return;
      const transition_reason = prompt('Reason for transition:') || '';
      const new_assignee_id_input = prompt('New Assignee User ID (optional):');
      const new_assignee_id = new_assignee_id_input ? Number(new_assignee_id_input) : undefined;

      const res = await fetch(`${API_BASE}/workflow/cases/${workflowId}/transition`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ to_stage, transition_reason, assignee_id: new_assignee_id })
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Failed with ${res.status}`);
      }
      await fetchWorkflows();
      setSuccess('Workflow transitioned successfully');
    } catch (err:any) {
      console.error(err);
      setError(`Transition failed: ${err.message || err}`);
    }
  };

  const handleQuickAdvance = async (workflowId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_BASE}/workflow/cases/${workflowId}/transition`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'advance' })
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Failed with ${res.status}`);
      }
      await fetchWorkflows();
      setSuccess('Advanced to next stage successfully');
    } catch (err:any) {
      console.error(err);
      setError(`Advance failed: ${err.message || err}`);
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'in_review':
        return 'bg-blue-100 text-blue-800';
      case 'qc':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'submitted':
        return 'bg-purple-100 text-purple-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'rework':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageDisplayName = (stage: string) => {
    switch (stage) {
      case 'draft':
        return 'Draft';
      case 'in_review':
        return 'In Review';
      case 'qc':
        return 'Quality Control';
      case 'approved':
        return 'Approved';
      case 'submitted':
        return 'Submitted';
      case 'closed':
        return 'Completed';
      case 'rejected':
        return 'Rejected';
      case 'rework':
        return 'Rework';
      default:
        return stage;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'normal':
        return 'bg-blue-100 text-blue-800';
      case 'low':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getSeverityColor = (severity: string) => {
    switch ((severity || '').toLowerCase()) {
      case 'severe':
        return 'bg-red-100 text-red-800';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800';
      case 'mild':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredWorkflows = workflows.filter(workflow => {
    if (filter === 'all') return true;
    if (filter === 'overdue') return workflow.is_overdue;
    if (filter === 'urgent') return workflow.is_urgent;
    return workflow.current_stage === filter;
  });

  const normalizeStageToBackend = (label: string): string => {
    if (!label) return '';
    const exact = uiToBackendStage[label];
    if (exact) return exact;
    const l = label.trim().toLowerCase();
    if (l.includes('quality')) return 'qc';
    if (l.includes('complet')) return 'closed';
    if (l.includes('review') && !l.includes('quality')) return 'in_review';
    if (l.includes('submiss')) return 'submitted';
    if (l.includes('draft') || l.includes('register')) return 'draft';
    return label;
  };

  const stagesAreEquivalent = (a: string, b: string): boolean => {
    const aa = (a || '').toLowerCase();
    const bb = (b || '').toLowerCase();
    if (aa === bb) return true;
    // closed/completed synonyms
    if ((aa === 'closed' && bb === 'completed') || (aa === 'completed' && bb === 'closed')) return true;
    // quality synonyms
    if ((aa === 'qc' && (bb === 'quality' || bb === 'quality check' || bb === 'quality control'))
      || (bb === 'qc' && (aa === 'quality' || aa === 'quality check' || aa === 'quality control'))) return true;
    return false;
  };

  const searchedWorkflows = filteredWorkflows
    .filter(wf => {
      if (!stageFilter) return true;
      const normalized = normalizeStageToBackend(stageFilter);
      const current = wf.current_stage || '';
      return stagesAreEquivalent(current, normalized) || stagesAreEquivalent(current, stageFilter);
    })
    .filter(wf => {
    const hay = [
      wf.record_id,
      wf.adverse_event?.description,
      wf.current_stage,
      wf.priority_level,
      wf.adverse_event?.reported_by
    ].map(v => String(v || '').toLowerCase()).join(' ');
    return hay.includes(searchTerm.toLowerCase());
  });

  const sortedWorkflows = [...searchedWorkflows].sort((a, b) => {
    const dir = sortOrder === 'asc' ? 1 : -1;
    const getVal = (wf: WorkflowCase) => {
      switch (sortKey) {
        case 'record_id': return wf.record_id || `ICSR-Proc-${wf.id}`;
        case 'title': return wf.adverse_event?.description || '';
        case 'priority_level': return wf.priority_level || '';
        case 'current_stage': return wf.current_stage || '';
        case 'current_stage_started_at': return wf.current_stage_started_at || '';
        case 'last_updated': return (wf as any).last_updated || wf.workflow_started_at || '';
        case 'created_at':
        default:
          return wf.created_at || '';
      }
    };
    const va = getVal(a);
    const vb = getVal(b);
    if (typeof va === 'string' && typeof vb === 'string') {
      return va.localeCompare(vb) * dir;
    }
    // Fallback
    return ((va as any) > (vb as any) ? 1 : (va as any) < (vb as any) ? -1 : 0) * dir;
  });

  const totalPages = Math.ceil(sortedWorkflows.length / rowsPerPage) || 1;
  const paginatedWorkflows = sortedWorkflows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading workflows...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Notifications */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md animate-fadeIn">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {success}
            <button onClick={() => setSuccess('')} className="ml-auto text-green-600 hover:text-green-800">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md animate-fadeIn">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
            <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div></div>
        <div className="flex items-center space-x-3">
          <button onClick={handleCreateWorkflow} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create Workflow
          </button>
        </div>
      </div>


      {/* Workflow List (MRT) */}
        
      {(() => {
        const columnHelper = createMRTColumnHelper<WorkflowCase>();
        const columns: any[] = [
          columnHelper.accessor(row => (row.record_id || (row.adverse_event_id ? `AE-${row.adverse_event_id}` : String(row.id))), { id: 'record_id', header: 'Record ID', size: 140 }),
          columnHelper.accessor(row => (row.adverse_event?.report_source || (row as any).source || 'user'), {
            id: 'source', header: 'Source', size: 110, filterVariant: 'select',
            Cell: ({ cell }) => {
              const v = String(cell.getValue() || '').toLowerCase();
              const label = v === 'literature' ? 'Literature' : (v || 'User');
              const cls = v === 'literature'
                ? 'bg-purple-100 text-purple-800'
                : 'bg-slate-100 text-slate-800';
              return <span className={`px-2 py-0.5 text-xs rounded ${cls}`}>{label}</span>;
            }
          }),
          columnHelper.accessor(row => (row.adverse_event?.description || `2025US${String(row.adverse_event_id).padStart(6,'0')}`), { id: 'title', header: 'Title / Identifier', size: 260 }),
          columnHelper.accessor('priority_level', { header: 'Priority', size: 110, filterVariant: 'select' }),
          columnHelper.accessor('current_stage', { header: 'Current Task', size: 160, filterVariant: 'select' }),
          columnHelper.accessor(row => (row.current_stage_started_at ? new Date(row.current_stage_started_at).toISOString().slice(0,10) : ''), { id: 'assignment_date', header: 'Assignment Date', size: 130, filterVariant: 'date-range' }),
          columnHelper.accessor(row => ((row as any).last_updated || row.workflow_started_at ? new Date(((row as any).last_updated || row.workflow_started_at)).toISOString().slice(0,10) : ''), { id: 'last_updated', header: 'Last Updated', size: 130, filterVariant: 'date-range' }),
          columnHelper.display({
            id: 'actions',
            header: 'Actions',
            size: 200,
            Cell: ({ row }) => (
              <div className="flex justify-end space-x-2">
                <button
                  onClick={(e) => { e.stopPropagation(); setViewingCaseId(row.original.id); setViewingCase(row.original); setViewMode('details'); }}
                  className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 bg-white/80 dark:bg-slate-800/40 hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  title="View Details"
                  aria-label={`View details for workflow ${row.original.id}`}
                >
                  View
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setViewMode('transition'); setViewingCaseId(row.original.id); setViewingCase(row.original); setTransitionStage(''); setTransitionReason(''); }}
                  className="px-2 py-1 text-sm rounded border border-indigo-400 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300 bg-white/80 dark:bg-slate-800/40 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                  title="Transition"
                >
                  Transition
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); fetchWorkflows(); }}
                  className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 bg-white/80 dark:bg-slate-800/40 hover:bg-gray-50 dark:hover:bg-slate-700"
                  title="Refresh"
                >
                  Refresh
                </button>
              </div>
            ),
          }),
        ];
        return <MRTTableAdapter<WorkflowCase> columns={columns} data={filteredWorkflows} />;
      })()}

      {viewingCaseId && viewingCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Workflow Case #{viewingCaseId}</h2>
              <button onClick={() => { setViewingCaseId(null); setViewingCase(null); }} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="mb-4">
              <WorkflowFlow
                currentStage={(viewingCase?.current_stage === 'approved' ? 'Submission' : viewingCase?.current_stage) || 'Register ICSR information'}
                stages={[ 'Register ICSR information','Review','Quality Check','Submission','Completed' ]}
                userRole={''}
                isLocked={false}
                recordId={String(viewingCase?.adverse_event_id || '')}
              />
            </div>
            <div className="flex items-center space-x-4 mb-4">
              <button onClick={() => setViewMode('details')} className={`text-sm ${viewMode==='details'?'font-semibold text-gray-900':'text-gray-500'}`}>Details</button>
              <button onClick={() => setViewMode('transition')} className={`text-sm ${viewMode==='transition'?'font-semibold text-gray-900':'text-gray-500'}`}>Transition</button>
            </div>

            {viewMode === 'details' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                <div><span className="text-gray-500">Record ID:</span> <span className="text-gray-900">{String(viewingCase.adverse_event_id)}</span></div>
                <div><span className="text-gray-500">Product:</span> <span className="text-gray-900">{viewingCase.adverse_event?.product_name || (viewingCase.adverse_event?.product_id ? `Product #${viewingCase.adverse_event.product_id}` : '-')}</span></div>
                <div className="md:col-span-2"><span className="text-gray-500">Description:</span> <span className="text-gray-900">{viewingCase.adverse_event?.description || '-'}</span></div>
                <div><span className="text-gray-500">Severity:</span> <span className="text-gray-900">{viewingCase.adverse_event?.severity || '-'}</span></div>
                <div><span className="text-gray-500">Serious:</span> <span className="text-gray-900">{viewingCase.adverse_event?.is_serious ? 'Yes' : 'No'}</span></div>
                <div><span className="text-gray-500">Reported By:</span> <span className="text-gray-900">{viewingCase.adverse_event?.reported_by || '-'}</span></div>
              </div>
            )}

            {viewMode === 'transition' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                  <div><span className="text-gray-500">Current Stage:</span> <span className="text-gray-900">{viewingCase.current_stage || '-'}</span></div>
                  <div><span className="text-gray-500">Assignee:</span> <span className="text-gray-900">{viewingCase.current_assignee?.full_name || viewingCase.current_assignee_id || '-'}</span></div>
                  <div><span className="text-gray-500">Priority:</span> <span className="text-gray-900">{viewingCase.priority_level || '-'}</span></div>
                  <div><span className="text-gray-500">SLA:</span> <span className="text-gray-900">{viewingCase.sla_deadline ? new Date(viewingCase.sla_deadline).toLocaleString() : '-'}</span></div>
                </div>
                <div className="border-t pt-4 mt-2">
                  <h3 className="text-md font-semibold mb-2 text-slate-900 dark:text-slate-100">Transition</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <select value={transitionStage} onChange={(e) => setTransitionStage(e.target.value)} className="border rounded px-3 py-2">
                      <option value="">Select next stage</option>
                      {diagramStages.map(s => (
                        <option key={s} value={s} disabled={uiToBackendStage[s] === viewingCase.current_stage}>{s}</option>
                      ))}
                    </select>
                    <input value={transitionAssignee} onChange={(e) => setTransitionAssignee(e.target.value)} className="border rounded px-3 py-2" placeholder="Assignee ID (optional)" />
                    <input value={transitionReason} onChange={(e) => setTransitionReason(e.target.value)} className="border rounded px-3 py-2" placeholder="Reason (optional)" />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button onClick={() => { setViewingCaseId(null); setViewingCase(null); }} className="px-4 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200">Close</button>
                    <button
                      onClick={async () => {
                        if (!transitionStage) { alert('Select a stage'); return; }
                        try {
                          const token = localStorage.getItem('token');
                          if (!token) return;
                          const backendStage = uiToBackendStage[transitionStage] || transitionStage;
                          const res = await fetch(`${API_BASE}/workflow/cases/${viewingCaseId}/transition`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify({ 
                              to_stage: backendStage, 
                              transition_reason: transitionReason, 
                              assignee_id: transitionAssignee ? Number(transitionAssignee) : undefined 
                            })
                          });
                          if (!res.ok) { const txt = await res.text(); throw new Error(txt || `Failed with ${res.status}`); }
                          await fetchWorkflows();
                          const updated = await res.json();
                          setViewingCase({ ...viewingCase, current_stage: updated.current_stage, current_assignee_id: updated.current_assignee_id });
                          setSuccess('Workflow transitioned successfully');
                          setViewingCaseId(null);
                          setViewingCase(null);
                        } catch (err:any) {
                          setError(`Transition failed: ${err.message || err}`);
                        }
                      }}
                      className="px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                    >
                      Apply Transition
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Workflow Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Create New Workflow</h2>
              <button 
                onClick={() => setShowCreateModal(false)} 
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleSubmitCreateWorkflow(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adverse Event *
                </label>
                <select
                  value={createFormData.adverse_event_id}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, adverse_event_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select an adverse event</option>
                  {adverseEvents.map((ae) => (
                    <option key={ae.id} value={ae.id}>
                      #{ae.id} - {ae.description || 'No description'} ({ae.severity || 'unknown'})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Initial Assignee
                </label>
                <select
                  value={createFormData.initial_assignee_id}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, initial_assignee_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select assignee (optional)</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.username || user.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority Level
                </label>
                <select
                  value={createFormData.priority_level}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, priority_level: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loadingCreate}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {loadingCreate ? 'Creating...' : 'Create Workflow'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Actions Modal */}
      {showBulkActions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Bulk Actions</h2>
              <button 
                onClick={() => setShowBulkActions(false)} 
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Action
                </label>
                <select
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select an action</option>
                  <option value="assign">Assign to User</option>
                  <option value="priority">Change Priority</option>
                  <option value="advance">Advance Stage</option>
                </select>
              </div>

              {bulkAction === 'assign' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assign to User
                  </label>
                  <select
                    value={bulkAssignee}
                    onChange={(e) => setBulkAssignee(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select user</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name || user.username || user.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {bulkAction === 'priority' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority Level
                  </label>
                  <select
                    value={bulkPriority}
                    onChange={(e) => setBulkPriority(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select priority</option>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  This action will be applied to <strong>{selectedWorkflows.length}</strong> selected workflows.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowBulkActions(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAction}
                disabled={loadingCreate || !bulkAction}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loadingCreate ? 'Processing...' : 'Apply Action'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowCaseList;


